/**
 * Scelta del registro di numerazione (CF08) — CL05.
 *
 * Chiude l'ultimo parametro del configuratore che prima diceva "non ancora
 * costruito": un campo `autoincrement` senza registro non puo' produrre un
 * codice, e il contratto lo rifiuta (`registryId` obbligatorio).
 *
 * I registri si caricano dall'API e non si inventano: l'elenco e' quello che il
 * server riconosce, e un id scritto a mano sarebbe un registro che non esiste.
 *
 * Un registro locale e uno d'organizzazione si mostrano **distinti**: emettono
 * dallo stesso contatore condiviso solo nel secondo caso, e confonderli
 * significherebbe credere che due campi non si rubino i numeri a vicenda.
 *
 * I progressivi **locali** si creano e si correggono **da qui**: la master del
 * modello non ha una gestione separata, e chiedere di andarla a cercare altrove
 * e' il modo in cui un parametro resta non configurato.
 */

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { create_UUID, getTranslation } from "common";
import { useSnackbar, useTranslations } from "hooks";
import type { RegistryRead } from "model/CustomFields";
import { apiError, customFieldsApi } from "api/customFieldsApi";
import DialogHeader from "components/dialogs/DialogHeader";
import RegistryEditorDialog from "./RegistryEditorDialog";
import { messageKeyFor } from "./errorMessages";
import { selectableRegistries } from "./registryDraft";

interface SequenceConfigurationProps {
  organizationId: string;
  /**
   * Il modello del campo che si sta configurando.
   *
   * Serve a distinguere i due autoincrementi: quello dell'organizzazione, che
   * vale per tutti e si recupera sempre, e quello **locale di questo** modello.
   * I registri locali di un altro modello non si offrono.
   *
   * Assente su un modello non ancora salvato: lì i locali non esistono.
   */
  modelId?: string | null;
  /**
   * Il nome del campo che si sta configurando.
   *
   * Un progressivo **locale** nasce da qui: il suo codice si ricava da questo
   * nome, e nella dialog di creazione non si scrive a mano.
   */
  fieldLabel?: string;
  /** L'id del registro scelto, o `null`. */
  value: string | null;
  onChange: (registryId: string) => void;
  /**
   * I registri già scelti da **altri** campi del modello.
   *
   * Vedi `selectableRegistries`: non si offrono, perché due campi sullo stesso
   * contatore si ruberebbero i numeri.
   */
  usedRegistryIds?: readonly string[];
  disabled?: boolean;
}

const SequenceConfiguration: React.FC<SequenceConfigurationProps> = ({
  organizationId,
  fieldLabel,
  modelId = null,
  value,
  onChange,
  usedRegistryIds,
  disabled = false,
}) => {
  const translations = useTranslations();
  const [registries, setRegistries] = useState<RegistryRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Il registro locale in modifica, `"new"` quando se ne sta creando uno. */
  const [editing, setEditing] = useState<RegistryRead | "new" | null>(null);
  /** Il registro locale di cui si sta confermando la cancellazione. */
  const [deleting, setDeleting] = useState<RegistryRead | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const { sendSnack } = useSnackbar();

  /**
   * Cancella (archivia) un progressivo locale.
   *
   * Se era quello associato al campo, l'associazione sparisce insieme a lui:
   * il `registryId` non punta più a niente, e la riga della master torna a
   * poter cambiare nome — il codice non è più inferito da lì.
   */
  const handleDelete = async () => {
    if (!deleting) return;
    setDeletePending(true);
    try {
      await customFieldsApi.deleteRegistry(organizationId, deleting.registryId, {
        operationId: create_UUID(),
        expectedRevision: deleting.revision,
      });
      if (value === deleting.registryId) onChange("");
      setDeleting(null);
      void carica();
    } catch (e) {
      sendSnack({
        message: getTranslation(translations, messageKeyFor(apiError(e))),
        type: "error",
      });
      setDeleting(null);
    } finally {
      setDeletePending(false);
    }
  };

  // Una lettura sola, riusata dall'apertura e dalla chiusura della dialog:
  // correggere un registro o crearne uno cambia l'elenco, e rileggerlo con una
  // seconda fetch scritta apposta sarebbe la stessa cosa detta due volte.
  const carica = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const page = await customFieldsApi.getRegistries(organizationId);
      setRegistries(page.items ?? []);
    } catch (e) {
      // L'elenco vuoto per un errore di rete sarebbe indistinguibile da "non
      // ci sono registri": si dice che e' fallito.
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void carica();
  }, [carica]);

  // Le due vie, separate in due elenchi sotto la loro intestazione.
  //
  // `selectableRegistries` decide cosa si può offrire — i locali di *altri*
  // modelli restano fuori, e i progressivi già presi da un altro campo pure,
  // tenendo visibile quello già scelto. Chiamata due volte perché le due sezioni
  // sono due insiemi: senza `modelId` restituisce quelli dell'organizzazione
  // (più l'eventuale scelto, che il filtro per scope toglie di qui), con il
  // `modelId` aggiunge i locali di questo modello.
  const orgChoices = selectableRegistries(registries, {
    selectedId: value,
    usedRegistryIds,
  }).filter((registry) => registry.scope === "organization");

  const modelChoices = selectableRegistries(registries, {
    modelId,
    selectedId: value,
    usedRegistryIds,
  }).filter((registry) => registry.scope === "model");

  // Una selezione che non è più nell'elenco — progressivo cancellato, oppure
  // di un altro modello — non è una selezione: il radio non mostra niente, ma
  // il `registryId` resterebbe nel config e il salvataggio passerebbe. Si
  // toglie, così il campo torna davvero «senza progressivo».
  useEffect(() => {
    if (!value || loading || error) return;
    const stillThere = [...orgChoices, ...modelChoices].some(
      (registry) => registry.registryId === value
    );
    if (!stillThere) onChange("");
  }, [value, loading, error, orgChoices, modelChoices, onChange]);

  if (loading) {
    return (
      <Stack alignItems="center" py={1}>
        <CircularProgress size={20} data-testid="cf-registry-loading" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="cf-registry-error">
        {getTranslation(translations, "custom_fields.sequence.load_failed")}
      </Alert>
    );
  }

  /** Una voce dell'elenco: nome e codice, senza l'ambito — lo dice la sezione. */
  const voceRegistro = (registry: RegistryRead) => (
    <FormControlLabel
      key={registry.registryId}
      value={registry.registryId}
      control={<Radio size="small" disabled={disabled} />}
      label={`${registry.name} (${registry.code})`}
      data-testid={`cf-registry-choice-${registry.registryId}`}
    />
  );

  /**
   * Una voce della sezione locale: la scelta del radio **e** il comando che
   * apre la modifica di quel progressivo. Configurare è un'azione diversa dallo
   * sceglierlo, e va dove sta la riga.
   */
  const voceRegistroLocale = (registry: RegistryRead) => (
    <Stack key={registry.registryId} direction="row" alignItems="center" gap={1}>
      {voceRegistro(registry)}
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        sx={{ textTransform: "none" }}
        onClick={() => setEditing(registry)}
        data-testid={`cf-registry-configure-${registry.registryId}`}
      >
        {getTranslation(translations, "custom_fields.action.configure")}
      </Button>
      {/* La cancellazione sta sulla riga del progressivo, accanto alla
          configurazione: è la stessa cosa che si amministra. Il blocco lo
          dichiara il server (`archiveBlocked`), come nell'elenco dell'azienda. */}
      <Tooltip
        title={
          Boolean(registry.archiveBlocked)
            ? getTranslation(translations, "custom_fields.sequence.blocked_active_process")
            : ""
        }
      >
        <span>
          <IconButton
            size="small"
            disabled={disabled || Boolean(registry.archiveBlocked)}
            onClick={() => setDeleting(registry)}
            data-testid={`cf-registry-delete-${registry.registryId}`}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const nessunoDisponibile = (
    <Typography variant="body2" color="text.secondary" data-testid="cf-registry-empty">
      {getTranslation(translations, "custom_fields.sequence.none_available")}
    </Typography>
  );

  return (
    <Stack gap={1}>
      <RadioGroup
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid="cf-registry-choices"
      >
        {/* Sezione 1 — i progressivi dell'organizzazione: condivisi con tutti i
            modelli, si scelgono, non si amministrano da qui. */}
        <Stack gap={1}>
          <Typography variant="subtitle2" data-testid="cf-registry-org-heading">
            {getTranslation(translations, "custom_fields.sequence.organization_heading")}
          </Typography>
          {orgChoices.length === 0 ? (
            nessunoDisponibile
          ) : (
            <Stack>{orgChoices.map(voceRegistro)}</Stack>
          )}
        </Stack>

        {/* Sezione 2 — i progressivi locali di questo modello: gli unici che si
            possono creare da qui, e l'unico posto in cui ha senso crearli. */}
        <Stack gap={1}>
          <Typography variant="subtitle2" data-testid="cf-registry-model-heading">
            {getTranslation(translations, "custom_fields.sequence.local_heading")}
          </Typography>

          {/* Un progressivo locale nasce **dentro** un modello: senza il suo id
              il server non saprebbe a chi attribuirlo, e il pulsante lo dice
              invece di fallire dopo. */}
          <Tooltip
            title={
              modelId
                ? ""
                : getTranslation(translations, "custom_fields.sequence.save_model_first")
            }
          >
            <span style={{ alignSelf: "flex-start" }}>
              <Button
                size="small"
                variant="text"
                disabled={disabled || !modelId}
                sx={{ textTransform: "none" }}
                onClick={() => setEditing("new")}
                data-testid="cf-registry-add"
              >
                {getTranslation(translations, "custom_fields.sequence.new_local")}
              </Button>
            </span>
          </Tooltip>

          {modelChoices.length === 0 ? (
            nessunoDisponibile
          ) : (
            <Stack>{modelChoices.map(voceRegistroLocale)}</Stack>
          )}
        </Stack>
      </RadioGroup>

      {/* Un registro disattivato non emette: sceglierlo sarebbe una scelta che
          non produce niente al primo avvio. */}
      {registries.some((r) => !r.active && r.registryId === value) && (
        <Alert severity="warning" data-testid="cf-registry-inactive">
          {getTranslation(translations, "custom_fields.sequence.inactive")}
        </Alert>
      )}

      <RegistryEditorDialog
        open={editing !== null}
        identity={{ organizationId, scope: "model", modelId }}
        // Solo in creazione: in modifica il codice è già quello emesso e non
        // si tocca comunque.
        suggestedCode={fieldLabel}
        registry={editing === "new" ? null : editing}
        onClose={(changed) => {
          setEditing(null);
          // Si rilegge solo se qualcosa è cambiato: un annullamento non ha
          // toccato niente, e rileggere farebbe lampeggiare l'elenco per nulla.
          if (changed) void carica();
        }}
      />

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        maxWidth="sm"
        fullWidth
        data-testid="cf-registry-delete-dialog"
      >
        <DialogHeader sx={{ px: 4 }} onClose={() => setDeleting(null)}>
          {getTranslation(translations, "custom_fields.sequence.delete")}
        </DialogHeader>
        <DialogContent>
          <Typography variant="body2" data-testid="cf-registry-delete-confirm-text">
            {getTranslation(translations, "custom_fields.sequence.delete_confirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} data-testid="cf-registry-delete-cancel">
            {getTranslation(translations, "custom_fields.action.cancel")}
          </Button>
          <Button
            color="error"
            disabled={deletePending}
            onClick={() => void handleDelete()}
            data-testid="cf-registry-delete-confirm"
          >
            {getTranslation(translations, "custom_fields.sequence.delete_confirm_action")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SequenceConfiguration;
